import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AppDispatch, RootState} from "../app/store";
import {getBaseUrl} from "./chatbotUtil";
import {
    BotType,
    FocusedVehicle,
    InitializationType,
    InputType,
    MessageRole,
    MessageType,
    QueryRequest,
    StripeMessageStatus,
    WidgetAppearanceConfigDTO,
    WidgetChatMessage,
    type WidgetChatResponse,
    WidgetPosition,
    WidgetSettings,
    WidgetUserInitializationResponse
} from "../api/generated";
import {stripeApi, widgetApi} from "../api/apiConfiguration";

export type ChatInput = {
    message: string,
    inputType: InputType
}

export const Currency = {
    Pound: 'POUND'
} as const;
export type Currency = typeof Currency[keyof typeof Currency];

export interface ChatbotState {
    botType?: BotType

    messages: WidgetChatMessage[];
    isWidgetOpen: boolean;
    chatId?: string;
    focusedVehicle: FocusedVehicle;
    thinking: boolean;
    welcomeBubbleOpen: boolean;
    focusedStripePaymentId?: number;

    initializationType: InitializationType;

    stripePublicKey?: string;
    stripeClientSecret?: string;
    stripeErrorMessage?: string;

    reservationPrice?: number;
    reservationCurrency?: Currency;

    isWidgetInitialized: boolean;
    websiteLocation?: string;
    botId?: string;
    pageFullUrl?: string;
    isMobile: boolean;
    hasWelcomeBubbleTimeoutElapsed: boolean;
    name?: string;

    showWidget: boolean;

    widgetAppearanceSettings: WidgetAppearanceConfigDTO;

    profile?: string;
    platformBaseURL?: string;
}

const initialState: ChatbotState = {
    showWidget: false,
    isMobile: false,
    isWidgetOpen: false,

    thinking: false,
    messages: [],
    welcomeBubbleOpen: true,
    hasWelcomeBubbleTimeoutElapsed: false,
    isWidgetInitialized: false,

    initializationType: InitializationType.Unsupported,

    focusedVehicle: {
        vin: '',
        vrm: ''
    },

    widgetAppearanceSettings: {
        mainColour: '#0C0126',
        buttonColour: '#0C0126',
        widgetPosition: WidgetPosition.Right,
        horizontalOffsetPx: 20,
        verticalOffsetPx: 20,
        hidePoweredBy: false,
        promptPopUpView: true,
        initialBotMessage: `Hi, I'm your AI powered sales assistant. I can help you with everything, from helping you find your perfect vehicle, right through to reserving it. What can I do for you today?`,
        defaultPopUpMessage: `Got a question? I am here to help!`,
        bubbleConvoStarters: [],
        chatConvoStarters: []
    } as WidgetAppearanceConfigDTO
};

export const sendStripeIntent = createAsyncThunk<string | undefined, number, { state: RootState }>(
    'chatbot/sendStripeIntent',
    async (payload, {getState, dispatch}) => {
        // @ts-ignore
        const state = getState();
        // @ts-ignore
        const botId = state.chatbot.botId;
        const chatId = state.chatbot.chatId!;
        // @ts-ignore
        const focusedStripePaymentIdFromState = state.chatbot.focusedStripePaymentId

        if (!botId) {
            throw new Error("Missed botId")
        }

        if (focusedStripePaymentIdFromState) {
            throw new Error("Please wait on previous payment completion")
        }

        dispatch(updateFocusedStripePaymentId(payload))

        return getStripeControllerApi(getState).createIntent({
            createStripeIntentRequest: {
                publicBotId: botId,
                chatId: chatId
            }
        });
    }
);

export const sendConfirmStripeIntent = createAsyncThunk<void, undefined, { state: RootState }>(
    'chatbot/sendConfirmStripeIntent',
    (_, {getState}) => {
        const stripeClientSecret = getState().chatbot.stripeClientSecret

        if (!stripeClientSecret) {
            throw new Error("stripeClientSecret must be present")
        }

        return getStripeControllerApi(getState).confirmIntent({clientSecret: stripeClientSecret})
    }
);

export const initializeUser = createAsyncThunk<WidgetUserInitializationResponse, undefined, { state: RootState }>(
    'chatbot/initializeUser',
    async (_, {getState}) => {
        const botId = selectExceptionally(getState().chatbot.botId);
        return getWidgetControllerApi(getState).initializeUser({publicBotId: botId})
    }
);

export const getChatHistory = createAsyncThunk<WidgetChatMessage[], undefined, { state: RootState }>(
    'chatbot/getChatHistory',
    async (_, {getState}) => {
        const state = getState();
        const chatbotState = state.chatbot;
        const chatId = selectExceptionally(chatbotState.chatId);

        return getWidgetControllerApi(getState).getChatHistory({chatId: chatId})
    }
);
export const possiblySendMessage = createAsyncThunk<void, ChatInput, { state: RootState, dispatch: AppDispatch }>(
    'chatbot/possiblySendMessage',
    async (payload, {getState, dispatch}) => {
        const {thinking} = getState().chatbot;
        if (!thinking) {
            dispatch(sendMessageToBot(payload));
        } else {
            console.warn("Bot is still thinking. Ignoring message:", payload.message);
        }
    }
);

export const sendMessageToBot = createAsyncThunk<
    WidgetChatResponse,
    { message: string, inputType: InputType },
    { state: RootState }
>(
    'chatbot/sendMessageToBot',
    async (payload, {getState, dispatch}) => {
        const messages = getState().chatbot.messages;

        if (messages.length == 0) {
            await dispatch(saveInitialBotMessage())
        }

        dispatch(addUserMessage(payload.message))
        const widgetChatRequest = prepareWidgetChatRequest(getState().chatbot, payload)

        return getWidgetControllerApi(getState).query(widgetChatRequest)
    }
);

export const addBotMessage = createAsyncThunk<WidgetChatMessage, string, { state: RootState, dispatch: AppDispatch }>(
    'chatbot/addBotMessage',
    async (payload, {getState, dispatch}) => {
        if (getState().chatbot.messages.length == 0) {
            await dispatch(saveInitialBotMessage())
        }

        const chatId = selectExceptionally(getState().chatbot.chatId)
        return getWidgetControllerApi(getState).addBotMessage({chatId, addBotMessageRequest: {botMessage: payload}})
    }
);

export const saveInitialBotMessage = createAsyncThunk<WidgetChatMessage, undefined, { state: RootState }>(
    'chatbot/saveInitialBotMessage',
    async (_, {getState}) => {
        const chatId = selectExceptionally(getState().chatbot.chatId)
        const initialBotMessage = getState().chatbot.widgetAppearanceSettings.initialBotMessage
        return getWidgetControllerApi(getState).saveInitialBotMessage({
            chatId,
            saveInitialBotMessageRequest: {
                initialBotMessage: initialBotMessage,
                initializationType: getState().chatbot.initializationType
            }
        })
    }
);

const prepareWidgetChatRequest = (chatbotState: ChatbotState, payload: ChatInput): QueryRequest => {
    return {
        widgetChatRequest: {
            publicBotId: selectExceptionally(chatbotState.botId),
            chatId: selectExceptionally(chatbotState.chatId),
            message: payload.message,
            pageFullUrl: selectExceptionally(chatbotState.pageFullUrl),
            inputType: payload.inputType,
            focusedVehicle: chatbotState.focusedVehicle,
            initializationType: chatbotState.initializationType,
            timestamp: new Date().toISOString()
        }
    }
}

export const getWidgetSettings = createAsyncThunk<WidgetSettings, string, { state: RootState }>(
    'chatbot/getWidgetSettings',
    async (payload, {getState}) => {
        return getWidgetControllerApi(getState).getWidgetSettings({publicBotId: payload})
    }
);

export const chatbotSlice = createSlice({
    name: 'chatbot',
    initialState,
    reducers: {
        initializeState: (state, {payload}) => {
            state.botId = payload.botId
            state.chatId = payload.chatId
            state.profile = payload.profile ?? "PROD"
            state.platformBaseURL = getBaseUrl("https://platform.motortech.ai", payload.platformBaseURL)
            state.pageFullUrl = payload.pageFullUrl
            state.isWidgetOpen = payload.open
            state.isMobile = payload.isMobile
            state.websiteLocation = new URL(payload.pageFullUrl).origin
            state.welcomeBubbleOpen = payload.welcomeBubbleOpen ?? true

            if (payload.focusedVehicle) {
                state.focusedVehicle = {
                    vin: payload.focusedVehicle.vin ?? payload.focusedVehicle.VIN,
                    vrm: payload.focusedVehicle.vrm ?? payload.focusedVehicle.VRM ?? payload.focusedVehicle.VRN
                }
            }

            state.isWidgetInitialized = true
        },
        openChatbot: (state) => {
            if (!state.isWidgetOpen) {
                state.isWidgetOpen = true;

                if (state.websiteLocation) {
                    window.parent.postMessage(
                        {
                            type: "update-local-storage",
                            key: "open",
                            value: JSON.stringify(true),
                            botId: state.botId
                        },
                        state.websiteLocation
                    )
                    window.parent.postMessage(
                        {
                            type: "update-local-storage",
                            key: "welcomeBubbleOpen",
                            value: JSON.stringify(false),
                            botId: state.botId
                        },
                        state.websiteLocation
                    )
                }

                state.welcomeBubbleOpen = false
            }
        },
        closeChatbot: (state) => {
            state.isWidgetOpen = false;

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "open",
                        value: JSON.stringify(false),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        focusVehicle: (state, {payload}: PayloadAction<FocusedVehicle>) => {
            state.focusedVehicle = payload;
            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "focusedVehicle",
                        value: JSON.stringify(payload),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        addUserMessage: (state, {payload}: PayloadAction<string>) => {
            state.messages.push({
                type: MessageType.Text,
                role: MessageRole.User,
                message: payload,
                timestamp: new Date().toISOString()
            })
        },
        markWelcomeBubbleTimeoutAsElapsed: (state) => {
            state.hasWelcomeBubbleTimeoutElapsed = true;
        },
        clearChatHistory: (state) => {
            state.stripeClientSecret = undefined;
            state.stripeErrorMessage = undefined;
            state.focusedVehicle = {
                vin: '',
                vrm: ''
            }
            state.thinking = false;
            state.chatId = undefined;
            state.messages = [];

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "remove-from-local-storage",
                        key: "chatId",
                        botId: state.botId
                    },
                    state.websiteLocation
                )
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "focusedVehicle",
                        value: JSON.stringify(state.focusedVehicle),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        },
        closeWelcomeBubble: (state) => {
            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "welcomeBubbleOpen",
                        value: JSON.stringify(false),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
            state.welcomeBubbleOpen = false;
        },
        updateInitializationType: (state, {payload}: PayloadAction<InitializationType>) => {
            state.initializationType = payload;
        },
        updateFirstBotMessage: (state, {payload}: PayloadAction<string>) => {
            state.widgetAppearanceSettings.initialBotMessage = payload;
        },
        updateWelcomeBubbleFirstMessage: (state, {payload}: PayloadAction<string>) => {
            if (payload.length > 0 && payload.length <= 130) {
                state.widgetAppearanceSettings.defaultPopUpMessage = payload;
            }
        },
        updateWelcomeBubbleConvoStarters: (state, {payload}: PayloadAction<string[]>) => {
            if (payload.length == 0) {
                state.widgetAppearanceSettings.bubbleConvoStarters = []
            } else {
                const newConvoStarters = payload.filter(p => p.length <= 30).slice(0, 2)
                if (newConvoStarters.length != 0) {
                    state.widgetAppearanceSettings.bubbleConvoStarters = newConvoStarters
                }
            }
        },
        updateFullUrl: (state, {payload}: PayloadAction<string>) => {
            state.pageFullUrl = payload;
        },
        updateFocusedStripePaymentId: (state, {payload}: PayloadAction<number>) => {
            state.focusedStripePaymentId = payload;
        },
        clearFocusedStripePaymentId: (state) => {
            state.focusedStripePaymentId = undefined;
        },
        updateStripeMessageStatusInMessage: (state, {payload}: PayloadAction<StripeMessageStatus>) => {
            state.messages.forEach(message => {
                if (message.type == MessageType.Stripe &&
                    message.stripePaymentId == state.focusedStripePaymentId &&
                    message.stripeMessageStatus != StripeMessageStatus.PaidConfirmed) {
                    message.stripeMessageStatus = payload;
                }
            })
        }
    },
    extraReducers: (builder) => {
        builder.addCase(initializeUser.fulfilled, (state, {payload}) => {
            const chatId = payload.chatId;
            state.chatId = chatId
            state.focusedVehicle = {
                vin: '',
                vrm: ''
            }

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "chatId",
                        value: JSON.stringify(chatId),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
                window.parent.postMessage(
                    {
                        type: "update-local-storage",
                        key: "focusedVehicle",
                        value: JSON.stringify(state.focusedVehicle),
                        botId: state.botId
                    },
                    state.websiteLocation
                )
            }
        })
        builder.addCase(saveInitialBotMessage.fulfilled, (state, {payload}) => {
            state.messages.push(payload)
        })
        builder.addCase(addBotMessage.fulfilled, (state, {payload}) => {
            state.messages.push(payload)
        })
        builder.addCase(getChatHistory.fulfilled, (state, {payload}) => {
            state.messages = payload
        })
        builder.addCase(getWidgetSettings.fulfilled, (state, {payload}) => {
            state.botType = payload.botType;
            state.name = payload.name;
            state.showWidget = payload.showWidget;
            state.stripePublicKey = payload.stripePublicKey;
            state.reservationPrice = payload.reservationPrice;
            state.reservationCurrency = payload.reservationCurrency;
            state.widgetAppearanceSettings = payload.widgetAppearanceSetting;

            const widgetPosition = payload.widgetAppearanceSetting.widgetPosition;
            const horizontalOffsetPx = payload.widgetAppearanceSetting.horizontalOffsetPx;
            const verticalOffsetPx = payload.widgetAppearanceSetting.verticalOffsetPx;

            if (state.websiteLocation) {
                window.parent.postMessage(
                    {
                        type: "change-widget-position",
                        bottom: verticalOffsetPx,
                        ...(widgetPosition == WidgetPosition.Right
                            ? {right: horizontalOffsetPx}
                            : {left: horizontalOffsetPx}),
                    },
                    state.websiteLocation
                )
            }
        })
        builder.addCase(sendConfirmStripeIntent.fulfilled, (state, {type}) => {
            chatbotSlice.caseReducers.updateStripeMessageStatusInMessage(state, {
                payload: StripeMessageStatus.PaidConfirmed,
                type
            })
            chatbotSlice.caseReducers.clearFocusedStripePaymentId(state)
        })
        builder.addCase(sendStripeIntent.fulfilled, (state, {payload, type}) => {
            state.stripeClientSecret = payload;
            chatbotSlice.caseReducers.updateStripeMessageStatusInMessage(state, {
                payload: StripeMessageStatus.PaidNotConfirmed,
                type
            })
            chatbotSlice.caseReducers.clearFocusedStripePaymentId(state)
        })
        builder.addCase(sendStripeIntent.pending, (state, {type}) => {
            chatbotSlice.caseReducers.updateStripeMessageStatusInMessage(state, {
                payload: StripeMessageStatus.Processing,
                type
            })
        })
        builder.addCase(sendStripeIntent.rejected, (state, {error, type}) => {
            state.stripeErrorMessage = error.message;
            chatbotSlice.caseReducers.updateStripeMessageStatusInMessage(state, {
                payload: StripeMessageStatus.NotPaid,
                type
            })
            chatbotSlice.caseReducers.clearFocusedStripePaymentId(state)
        })
        builder.addCase(sendMessageToBot.fulfilled, (state, {payload}) => {
            state.thinking = false;
            state.messages.push(...payload.widgetChatMessages)
        })
        builder.addCase(sendMessageToBot.pending, (state, action) => {
            state.thinking = true;
        })
        builder.addCase(sendMessageToBot.rejected, (state, action) => {
            state.thinking = false;
        })
    },
});

export const {
    openChatbot,
    closeChatbot,
    focusVehicle,
    updateFullUrl,
    addUserMessage,
    initializeState,
    clearChatHistory,
    closeWelcomeBubble,
    updateFirstBotMessage,
    updateInitializationType,
    updateFocusedStripePaymentId,
    updateWelcomeBubbleFirstMessage,
    updateWelcomeBubbleConvoStarters,
    markWelcomeBubbleTimeoutAsElapsed
} = chatbotSlice.actions;

export const profileSelector = (state: RootState): string | undefined => state.chatbot.profile;
export const platformBaseURLSelector = (state: RootState): string | undefined => state.chatbot.platformBaseURL;

export const botIdSelector = (state: RootState): string | undefined => state.chatbot.botId;
export const showWidgetSelector = (state: RootState): boolean => state.chatbot.showWidget;
export const isWidgetOpenSelector = (state: RootState): boolean => state.chatbot.isWidgetOpen;
export const botNameSelector = (state: RootState): string | undefined => state.chatbot.name;
export const botTypeSelector = (state: RootState): BotType | undefined => state.chatbot.botType;
export const isMobileSelector = (state: RootState): boolean => state.chatbot.isMobile;
export const isBotThinkingSelector = (state: RootState): boolean => state.chatbot.thinking;
export const messagesSelector = (state: RootState): WidgetChatMessage[] => state.chatbot.messages;
export const welcomeBubbleOpenSelector = (state: RootState): boolean => state.chatbot.welcomeBubbleOpen && state.chatbot.widgetAppearanceSettings.promptPopUpView && state.chatbot.hasWelcomeBubbleTimeoutElapsed;
export const isWidgetInitializedSelector = (state: RootState): boolean => state.chatbot.isWidgetInitialized;
export const websiteLocationSelector = (state: RootState): string => state.chatbot.websiteLocation || "/";
export const focusedVehicleSelector = (state: RootState): string | undefined => state.chatbot.focusedVehicle.vin;

export const stripePublicKeySelector = (state: RootState): string | undefined => state.chatbot.stripePublicKey;
export const stripeClientSecretSelector = (state: RootState): string | undefined => state.chatbot.stripeClientSecret;
export const stripeErrorMessageSelector = (state: RootState): string | undefined => state.chatbot.stripeErrorMessage;

export const reservationPriceSelector = (state: RootState): number | undefined => state.chatbot.reservationPrice;
export const reservationCurrencySelector = (state: RootState): Currency | undefined => state.chatbot.reservationCurrency;

export const mainColourSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.mainColour.toUpperCase()
export const buttonColourSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.buttonColour;
export const initialMessageSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.initialBotMessage;
export const defaultPopUpMessageSelector = (state: RootState): string => state.chatbot.widgetAppearanceSettings.defaultPopUpMessage || "Got a question? I am here to help!";
export const chatConvoStartersSelector = (state: RootState): string[] => state.chatbot.widgetAppearanceSettings.chatConvoStarters || [];
export const bubbleConvoStartersSelector = (state: RootState): string[] => state.chatbot.widgetAppearanceSettings.bubbleConvoStarters || [];
export const verticalOffsetSelector = (state: RootState): number => state.chatbot.widgetAppearanceSettings.verticalOffsetPx;
export const horizontalOffsetSelector = (state: RootState): number => state.chatbot.widgetAppearanceSettings.horizontalOffsetPx;
export const hidePoweredBySelector = (state: RootState): boolean => state.chatbot.widgetAppearanceSettings.hidePoweredBy;
export const widgetPositionSelector = (state: RootState): WidgetPosition => state.chatbot.widgetAppearanceSettings.widgetPosition;

export default chatbotSlice.reducer;

const getStripeControllerApi = (getState: () => any) => {
    const state = getState();
    const platformBaseURL = state.chatbot.platformBaseURL;

    if (!platformBaseURL) {
        throw new Error("Missed platformBaseURL")
    }

    return stripeApi(platformBaseURL);
}

export const getWidgetControllerApi = (getState: () => any) => {
    const state = getState();
    const platformBaseURL = state.chatbot.platformBaseURL;

    if (!platformBaseURL) {
        throw new Error("Missed platformBaseURL")
    }

    return widgetApi(platformBaseURL);
}

export const selectExceptionally = <T>(item: T | undefined | null): T => {
    if (item !== undefined && item !== null) {
        return item;
    } else {
        console.log('Item was not yet initialized')
        throw new Error('Item was not yet initialized')
    }
}
